import type { Markup, Point } from '@/types';
import { dimensionGeometry } from '@/core/geometry/dimension';
import { getBoxVertices } from './bounds';
import { normalizeMarkupGeometry } from './normalize';

export function getMarkupVertices(markup: Markup, allMarkups?: readonly Markup[]): Point[] {
    if (!markup?.points) return [];

    if (markup.type === 'group') {
        if (!allMarkups) return [];
        const vertices: Point[] = [];
        for (const childId of markup.childIds) {
            const child = allMarkups.find((candidate) => candidate.id === childId);
            if (child) vertices.push(...getMarkupVertices(child, allMarkups));
        }
        return vertices;
    }

    return [...markup.points];
}

export function getMarkupSegments(markup: Markup): Array<{ start: Point; end: Point }> {
    const geometry = normalizeMarkupGeometry(markup);
    if (!geometry) return [];

    switch (geometry.kind) {
        case 'segment':
            return [{ start: geometry.a, end: geometry.b }];
        case 'polyline':
            return geometry.points.slice(0, -1).map((point, index) => ({
                start: point,
                end: geometry.points[index + 1],
            }));
        case 'polygon': {
            const points = geometry.points;
            return points.map((point, index) => ({
                start: point,
                end: points[(index + 1) % points.length],
            }));
        }
        case 'box': {
            const vertices = getBoxVertices(geometry.box);
            return vertices.map((point, index) => ({
                start: point,
                end: vertices[(index + 1) % vertices.length],
            }));
        }
        case 'dimension': {
            const { a1, a2 } = dimensionGeometry(geometry.a, geometry.b, geometry.offset);
            return [
                { start: geometry.a, end: geometry.b },
                { start: a1, end: a2 },
                { start: geometry.a, end: a1 },
                { start: geometry.b, end: a2 },
            ];
        }
        default:
            return [];
    }
}
